import React from 'react';
import { bool, func, node, number, string } from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import classNames from 'classnames';
import { map } from 'ramda';

import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';
import { typeOfPoolSearchParams } from '../../util/search';
import { isSeason } from '../../util/dates';
import { Checkbox, Emoji, SecondaryButton, Switch } from '../../components';

import css from './SearchFiltersPrimary.css';
import { SEARCH_SORT_NEAREST, SEARCH_SORT_RELEVANCE } from '../../constants/listing';
import { isMobile } from 'react-device-detect';
import IconFilter from '../IconFilter/IconFilter';

const SearchFiltersPrimaryComponent = (props) => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    intl,
    listingsAreLoaded,
    resultsCount,
    searchInProgress,
    isSecondaryFiltersOpen,
    toggleSecondaryFiltersOpen,
    selectedSecondaryFiltersCount,
    handleShowSearchMap,
    showSearchMap,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const hasNoResult = listingsAreLoaded && resultsCount === 0;
  const classes = classNames(rootClassName || css.root, className);
  const toggleSecondaryFiltersOpenButtonClasses =
    isSecondaryFiltersOpen || selectedSecondaryFiltersCount > 0
      ? css.searchFiltersPanelOpen
      : css.searchFiltersPanelClosed;
  const toggleSecondaryFiltersOpenButton = toggleSecondaryFiltersOpen ? (
    <button
      className={toggleSecondaryFiltersOpenButtonClasses}
      onClick={() => {
        toggleSecondaryFiltersOpen(!isSecondaryFiltersOpen);
      }}
    >
      <IconFilter/> <FormattedMessage
        id="SearchFiltersPrimary.moreFiltersButton"
        values={{ count: selectedSecondaryFiltersCount }}
      />
    </button>
  ) : null;
  const openMapText = <FormattedMessage id="SearchFiltersPrimary.showMap" />;
  const typeOfPoolFilters = [
    ...(isSeason()
      ? []
      : [
          {
            value: 'indoor',
            text: intl.formatMessage({ id: 'SearchFiltersPrimary.indoor' }),
            symbol: '0x1f3e0',
          },
        ]),
    {
      value: 'jacuzzi',
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.jacuzzi' }),
      symbol: '0x1F6C1',
    },
  ];
  const sorts = [
    {
      value: SEARCH_SORT_NEAREST,
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.SearchListingSortByNearest' }),
      symbol: '0x1F30D',
    },
    {
      value: SEARCH_SORT_RELEVANCE,
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.SearchListingSortByPertinence' }),
      symbol: '0x2B50',
    },
  ];
  const filters = [
    {
      value: 'instant',
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.InstantBookingShort' }),
      symbol: '0x26A1',
    },
  ];
  const primaryRuleFilters = [
    {
      value: 'naturism',
      queryParam: 'pub_naturistAllowed',
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.naturism' }),
      symbol: '0x1F341',
      type: 'boolean',
    },
    {
      value: 'burkini',
      queryParam: 'pub_burkiniAllowed',
      text: intl.formatMessage({ id: 'SearchFiltersPrimary.burkini' }),
      symbol: '0x01FA71',
      type: 'boolean',
    },
    // {
    //   value: 'ownerPresence',
    //   queryParam: 'pub_proprietairesPresents',
    //   text: intl.formatMessage({ id: 'SearchFiltersPrimary.ownerPresence' }),
    //   symbol: '0x1F464',
    //   type: 'enum',
    //   enumValues: ['oui', 'non', 'occasionnellement'],
    // },
  ];
  const parsedSearch = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const applyTypeOfPoolFilter = (value) => {
    const { pub_extras, pub_policyEvent, typeOfPool, ...restOfSearchParams } = parsedSearch;
    const clearFilter = typeOfPool === value;
    const searchParams = {
      ...restOfSearchParams,
      page: 1,
      ...(clearFilter ? {} : typeOfPoolSearchParams(value)),
      ...(clearFilter ? {} : { typeOfPool: value }),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };
  const applySort = (value) => {
    const { _sort, ...restOfSearchParams } = parsedSearch;
    const clearFilter = _sort === value;
    const searchParams = {
      ...restOfSearchParams,
      page: 1,
      // ...(clearFilter ? {} : typeOfPoolSearchParams(value)),
      ...(clearFilter ? {} : { _sort: value }),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };
  const applyFilter = (value) => {
    const { type, ...restOfSearchParams } = parsedSearch;
    const clearFilter = type === value;
    const searchParams = {
      ...restOfSearchParams,
      page: 1,
      // ...(clearFilter ? {} : typeOfPoolSearchParams(value)),
      ...(clearFilter ? {} : { type: value }),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };
  const applyPrimaryRuleFilter = (filterConfig) => {
    const { queryParam, type, enumValues } = filterConfig;
    const { [queryParam]: currentValue, ...restOfSearchParams } = parsedSearch;

    let nextParamValue;
    if (type === 'boolean') {
      // toggle true/undefined
      nextParamValue = currentValue === 'true' || currentValue === true ? undefined : 'true';
    } else if (type === 'enum' && Array.isArray(enumValues) && enumValues.length > 0) {
      // cycle over allowed values, then clear
      const currentIndex = enumValues.indexOf(currentValue);
      const hasNext = currentIndex >= 0 && currentIndex < enumValues.length - 1;
      nextParamValue =
        currentIndex === -1 ? enumValues[0] : hasNext ? enumValues[currentIndex + 1] : undefined;
    }

    const searchParams = {
      ...restOfSearchParams,
      page: 1,
      ...(nextParamValue ? { [queryParam]: nextParamValue } : {}),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };

  return (
    <div className={classes}>
      <div className={css.searchOptions}>
        {listingsAreLoaded ? (
          <div className={css.searchResultSummary}>
            <span className={css.resultsFound}>
              <FormattedMessage
                id="SearchFiltersPrimary.foundResults"
                values={{ count: resultsCount }}
              />
            </span>
          </div>
        ) : null}
        {sortByComponent}
      </div>
      <div className={css.filters}>
        <div className={css.typeOfPoolFilters}>
          {map((filter) => {
            const isActive =
              filter.value === parsedSearch._sort ||
              (!parsedSearch._sort && filter.value === SEARCH_SORT_NEAREST);
            const buttonClasses = classNames(css.typeOfPoolButton, {
              [css.active]: isActive,
            });

            return (
              <SecondaryButton
                key={filter.value}
                className={buttonClasses}
                onClick={() => applySort(filter.value)}
              >
                <Emoji className={css.emoji} symbol={filter.symbol} />
                <span className={css.buttonText}>{filter.text}</span>
                <Checkbox className={css.checkbox} checked={isActive} />
              </SecondaryButton>
            );
          }, sorts)}
          {map((filter) => {
            const isActive = filter.value === parsedSearch.type;
            const buttonClasses = classNames(css.typeOfPoolButton, {
              [css.active]: isActive,
            });

            return (
              <SecondaryButton
                key={filter.value}
                className={buttonClasses}
                onClick={() => applyFilter(filter.value)}
              >
                <Emoji className={css.emoji} symbol={filter.symbol} />
                <span className={css.buttonText}>{filter.text}</span>
                <Checkbox className={css.checkbox} checked={isActive} />
              </SecondaryButton>
            );
          }, filters)}
          {map((filter) => {
            const isActive = filter.value === parsedSearch.typeOfPool;
            const buttonClasses = classNames(css.typeOfPoolButton, {
              [css.active]: isActive,
            });

            return (
              <SecondaryButton
                key={filter.value}
                className={buttonClasses}
                onClick={() => applyTypeOfPoolFilter(filter.value)}
              >
                <Emoji className={css.emoji} symbol={filter.symbol} />
                <span className={css.buttonText}>{filter.text}</span>
                <Checkbox className={css.checkbox} checked={isActive} />
              </SecondaryButton>
            );
          }, typeOfPoolFilters)}
          {map((filter) => {
            const isActive = !!parsedSearch[filter.queryParam];
            const buttonClasses = classNames(css.typeOfPoolButton, {
              [css.active]: isActive,
            });

            return (
              <SecondaryButton
                key={filter.value}
                className={buttonClasses}
                onClick={() => applyPrimaryRuleFilter(filter)}
              >
                <Emoji className={css.emoji} symbol={filter.symbol} />
                <span className={css.buttonText}>{filter.text}</span>
                <Checkbox className={css.checkbox} checked={isActive} />
              </SecondaryButton>
            );
          }, primaryRuleFilters)}
        </div>
        <div className={css.otherFilters}>
          {children}
          {toggleSecondaryFiltersOpenButton}
          {!isMobile && (
            <div className={css.map}>
              <div className={css.box}>
                <span className={css.text}>{openMapText}</span>
                <Switch onClick={handleShowSearchMap} value={showSearchMap} />
              </div>
            </div>
          )}
        </div>
      </div>

      {hasNoResult ? (
        <div className={css.noSearchResults}>
          <FormattedMessage id="SearchFiltersPrimary.noResults" />
        </div>
      ) : null}

      {searchInProgress ? (
        <div className={css.loadingResults}>
          <FormattedMessage id="SearchFiltersPrimary.loadingResults" />
        </div>
      ) : null}
    </div>
  );
};

SearchFiltersPrimaryComponent.defaultProps = {
  rootClassName: null,
  className: null,
  resultsCount: null,
  searchInProgress: false,
  isSecondaryFiltersOpen: false,
  toggleSecondaryFiltersOpen: null,
  selectedSecondaryFiltersCount: 0,
  sortByComponent: null,
};

SearchFiltersPrimaryComponent.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
  listingsAreLoaded: bool.isRequired,
  resultsCount: number,
  searchInProgress: bool,
  isSecondaryFiltersOpen: bool,
  toggleSecondaryFiltersOpen: func,
  selectedSecondaryFiltersCount: number,
  sortByComponent: node,
};

const SearchFiltersPrimary = SearchFiltersPrimaryComponent;

export default injectIntl(SearchFiltersPrimary);
